import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../../api/header";
import apiClient from "../../../api/apiClient";
import { toast } from "sonner";
import { all_routes } from "../../../Router/all_routes";

// UI Components
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader, PageHeaderHeading } from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReactSelect } from "@/components/ui/react-select";
import { Switch } from "@/components/ui/switch";
import DefaultEditor from "react-simple-wysiwyg";
import Modal from "@/components/ui/modal";
import CustomerModal from "../../../components/modals/CustomerModal";
import { Textarea } from "@/components/ui/textarea";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Icons
import {
  Plus,
  PlusCircle,
  Trash2,
  Save,
  ArrowLeft,
  Settings,
} from "lucide-react";

// Generate a random ID for new items
const generateId = () => Math.random().toString(36).substr(2, 9);

const AddAssembleJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;
  const queryClient = useQueryClient();
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    customer_id: "",
    customer: null,
    assemble_config_id: "",
    assemble_config: null,
    assigned_employee_id: "",
    assigned_employee: null,
    assembly_fee: "",
    notes: "",
    show_component_prices: false,
  });

  const { data: jobData, isLoading: isJobLoading } = useQuery({
    queryKey: ["assemble-job", id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.ASSEMBLE.JOB_DETAILS(id));
      return res.data?.data || res.data;
    },
    enabled: !!id,
  });

  const [items, setItems] = useState([
    {
      id: generateId(),
      product_id: "",
      product: null,
      quantity: 1,
      unit_cost: "",
      unit_price: "",
      inventory_batch_id: "",
      inventory_serial_id: "",
    },
  ]);

  useEffect(() => {
    if (jobData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData({
        name: jobData.name || "",
        customer_id: jobData.customer_id || "",
        customer: jobData.customer || null,
        assemble_config_id: jobData.assemble_config_id || "",
        assemble_config: jobData.assemble_config || null,
        assigned_employee_id: jobData.assigned_employee_id || "",
        assigned_employee: jobData.assigned_employee || null,
        assembly_fee: jobData.assembly_fee || "",
        notes: jobData.notes || "",
        show_component_prices: !!jobData.show_component_prices,
      });
      if (jobData.items && jobData.items.length > 0) {
        setItems(
          jobData.items.map((item) => ({
            id: generateId(),
            product_id: item.product_id || "",
            product: item.product || null,
            quantity: item.quantity || 1,
            unit_cost: item.unit_cost || "",
            unit_price: item.unit_price || "",
            inventory_batch_id: item.inventory_batch_id || "",
            inventory_serial_id: item.inventory_serial_id || "",
          })),
        );
      }
    }
  }, [jobData]);

  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState("");
  const [configSearch, setConfigSearch] = useState("");
  const [debouncedConfigSearch, setDebouncedConfigSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentItemConfig, setCurrentItemConfig] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(productSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [productSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerSearch(customerSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConfigSearch(configSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [configSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [employeeSearch]);

  // Infinite query for products
  const {
    data: productsData,
    fetchNextPage: fetchNextProductsPage,
    hasNextPage: hasNextProductsPage,
    isFetchingNextPage: isFetchingNextProductsPage,
    isLoading: isProductsLoading,
  } = useInfiniteQuery({
    queryKey: ["assemble-products", debouncedSearch, store_id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get(
        API_ENDPOINTS.INVENTORY.POS_PRODUCTS,
        {
          params: {
            perPage: 15,
            page: pageParam,
            search: debouncedSearch,
            store_id,
          },
        },
      );
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (meta && meta.current_page < meta.last_page) {
        return meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000 * 5,
  });

  const products = useMemo(() => {
    if (!productsData) return [];
    return productsData.pages.flatMap((page) => page.data || []);
  }, [productsData]);

  const productsLoading = isProductsLoading || isFetchingNextProductsPage;

  // Infinite query for customers
  const {
    data: customersData,
    fetchNextPage: fetchNextCustomersPage,
    hasNextPage: hasNextCustomersPage,
    isFetchingNextPage: isFetchingNextCustomersPage,
    isLoading: isCustomersLoading,
  } = useInfiniteQuery({
    queryKey: ["assemble-customers", debouncedCustomerSearch, store_id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: {
          perPage: 15,
          page: pageParam,
          search: debouncedCustomerSearch,
          store_id,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (meta && meta.current_page < meta.last_page) {
        return meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000 * 5,
  });

  const customers = useMemo(() => {
    if (!customersData) return [];
    return customersData.pages.flatMap((page) => page.data || []);
  }, [customersData]);

  const customersLoading = isCustomersLoading || isFetchingNextCustomersPage;

  // Infinite query for configs
  const {
    data: configsData,
    fetchNextPage: fetchNextConfigsPage,
    hasNextPage: hasNextConfigsPage,
    isFetchingNextPage: isFetchingNextConfigsPage,
    isLoading: isConfigsLoading,
  } = useInfiniteQuery({
    queryKey: ["assemble-configs-list", debouncedConfigSearch, store_id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get(API_ENDPOINTS.ASSEMBLE.CONFIGS, {
        params: {
          perPage: 15,
          page: pageParam,
          search: debouncedConfigSearch,
          store_id,
          status: "active",
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (meta && meta.current_page < meta.last_page) {
        return meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000 * 5,
  });

  const configs = useMemo(() => {
    if (!configsData) return [];
    return configsData.pages.flatMap((page) => page.data || []);
  }, [configsData]);

  const configsLoading = isConfigsLoading || isFetchingNextConfigsPage;

  // Infinite query for employees
  const {
    data: employeesData,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
    isFetchingNextPage: isFetchingNextEmployeesPage,
    isLoading: isEmployeesLoading,
  } = useInfiniteQuery({
    queryKey: ["assemble-employees", debouncedEmployeeSearch, store_id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES, {
        params: {
          perPage: 15,
          page: pageParam,
          search: debouncedEmployeeSearch,
          store_id,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (meta && meta.current_page < meta.last_page) {
        return meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000 * 5,
  });

  const employees = useMemo(() => {
    if (!employeesData) return [];
    return employeesData.pages.flatMap((page) => page.data || []);
  }, [employeesData]);

  const employeesLoading = isEmployeesLoading || isFetchingNextEmployeesPage;

  const handleConfigChange = async (selectedOption) => {
    const configId = selectedOption ? selectedOption.value : "";
    const selectedConfig = configs.find((c) => c.id === configId);

    setFormData({
      ...formData,
      assemble_config_id: configId,
      assemble_config: selectedConfig || null,
    });

    if (configId) {
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.ASSEMBLE.CONFIG_DETAILS(configId),
        );
        const data = res.data?.data || res.data;
        if (data) {
          setFormData((prev) => ({
            ...prev,
            name: data.name || prev.name,
            show_component_prices:
              data.show_component_prices !== undefined
                ? !!data.show_component_prices
                : prev.show_component_prices,
          }));
          if (data.items && data.items.length > 0) {
            setItems(
              data.items.map((item) => ({
                id: generateId(),
                product_id: item.product_id || "",
                product: item.product || null,
                quantity: item.quantity || 1,
                unit_cost: item.product?.cost || "",
                unit_price:
                  item.product?.sell_price || item.product?.price || "",
                inventory_batch_id: "",
                inventory_serial_id: "",
              })),
            );
          }
        }
      } catch {
        toast.error("Failed to fetch assemble config details");
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        name: "",
        show_component_prices: false,
      }));
      setItems([
        {
          id: generateId(),
          product_id: "",
          product: null,
          quantity: 1,
          unit_cost: "",
          unit_price: "",
          inventory_batch_id: "",
          inventory_serial_id: "",
        },
      ]);
    }
  };

  const orderItemChange = (index, key, value) => {
    const newItems = [...items];
    newItems[index][key] = value;

    // Auto-fill price and cost when product is selected
    if (key === "product" && value) {
      newItems[index].unit_price = value.sell_price || value.price || 0;
      newItems[index].unit_cost = value.cost || 0;
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        id: generateId(),
        product_id: "",
        product: null,
        quantity: 1,
        unit_cost: "",
        unit_price: "",
        inventory_batch_id: "",
        inventory_serial_id: "",
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const openConfigModal = (index, product = null) => {
    const item = items[index];
    const prod = product || item.product;
    if (!prod) return;

    setEditingIndex(index);
    setCurrentItemConfig({
      product_id: prod.id,
      product: prod,
      quantity: item.quantity || 1,
      unit_price: item.unit_price || prod.sell_price || prod.price || 0,
      inventory_batch_id: item.inventory_batch_id || "",
      serial_numbers: item.inventory_serial_id
        ? [item.inventory_serial_id]
        : [],
      notes: item.notes || "",
    });
    setShowConfigModal(true);
  };

  const handleSaveItemConfig = () => {
    if (currentItemConfig.quantity < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }

    const prod = currentItemConfig.product;
    const trackingType = prod?.manage_inventory
      ? prod.tracking_type || (prod.is_serialized ? "serial" : "none")
      : "none";

    if (trackingType === "serial") {
      if (
        (currentItemConfig.serial_numbers?.length || 0) !==
        currentItemConfig.quantity
      ) {
        toast.error(
          `Please select exactly ${currentItemConfig.quantity} serial numbers`,
        );
        return;
      }
    }

    if (trackingType === "standard" && !currentItemConfig.inventory_batch_id) {
      toast.error("Please select a batch");
      return;
    }

    const newItems = [...items];

    // If it's a serialized product with multiple quantity, split into multiple rows
    if (trackingType === "serial" && currentItemConfig.quantity > 1) {
      const baseItem = {
        product_id: currentItemConfig.product_id,
        product: currentItemConfig.product,
        quantity: 1,
        unit_price: currentItemConfig.unit_price,
        unit_cost: currentItemConfig.product?.cost || 0,
        inventory_batch_id: "",
        notes: currentItemConfig.notes || "",
      };

      const newRows = currentItemConfig.serial_numbers.map((serial) => ({
        ...baseItem,
        id: generateId(),
        inventory_serial_id: serial,
      }));

      newItems.splice(editingIndex, 1, ...newRows);
    } else {
      newItems[editingIndex] = {
        ...newItems[editingIndex],
        product_id: currentItemConfig.product_id,
        product: currentItemConfig.product,
        quantity: currentItemConfig.quantity,
        unit_price: currentItemConfig.unit_price,
        inventory_batch_id: currentItemConfig.inventory_batch_id || "",
        inventory_serial_id: currentItemConfig.serial_numbers?.[0] || "",
        notes: currentItemConfig.notes || "",
      };

      if (newItems[editingIndex].unit_cost === "") {
        newItems[editingIndex].unit_cost = currentItemConfig.product?.cost || 0;
      }
    }

    setItems(newItems);
    setShowConfigModal(false);
  };

  const createJobMutation = useMutation({
    mutationFn: async (payload) => {
      if (id) {
        return await apiClient.put(
          API_ENDPOINTS.ASSEMBLE.JOB_DETAILS(id),
          payload,
        );
      }
      return await apiClient.post(API_ENDPOINTS.ASSEMBLE.JOBS, payload);
    },
    onSuccess: () => {
      toast.success(`Assemble job ${id ? "updated" : "created"} successfully`);
      queryClient.invalidateQueries({ queryKey: ["assemble-jobs"] });
      navigate(all_routes.assembleJobs); // Navigate to job list
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to create assemble job",
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Job name is required");
      return;
    }

    if (!store_id) {
      toast.error("Store ID is required");
      return;
    }

    if (items.length === 0) {
      toast.error("Please add at least one component");
      return;
    }

    const invalidItem = items.find(
      (i) => !i.product_id || parseInt(i.quantity, 10) < 1,
    );
    if (invalidItem) {
      toast.error(
        "Please ensure all components have a product selected and valid quantity",
      );
      return;
    }

    const missingTrackingData = items.find((item) => {
      if (!item.product) return false;
      const trackingType = item.product.manage_inventory
        ? item.product.tracking_type ||
        (item.product.is_serialized ? "serial" : "none")
        : "none";

      if (trackingType !== "none") {
        return !item.inventory_serial_id && !item.inventory_batch_id;
      }
      return false;
    });

    if (missingTrackingData) {
      toast.error(
        "Please ensure all tracked components have a serial number or batch selected",
      );
      return;
    }

    const payload = {
      store_id,
      name: formData.name,
      customer_id: formData.customer_id || null,
      assigned_employee_id: formData.assigned_employee_id || null,
      assemble_config_id: formData.assemble_config_id || null,
      assembly_fee: formData.assembly_fee
        ? parseFloat(formData.assembly_fee)
        : null,
      notes: formData.notes || null,
      show_component_prices: formData.show_component_prices,
      items: items.map((item) => ({
        product_id: item.product_id,
        quantity: parseInt(item.quantity, 10),
        unit_cost: item.unit_cost ? parseFloat(item.unit_cost) : null,
        unit_price: item.unit_price ? parseFloat(item.unit_price) : null,
        inventory_batch_id: item.inventory_batch_id || null,
        inventory_serial_id: item.inventory_serial_id || null,
      })),
    };

    createJobMutation.mutate(payload);
  };

  if (isJobLoading) {
    return <div className="p-8 text-center">Loading job details...</div>;
  }

  return (
    <PageContainer>
      <PageHeader>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeaderHeading
            title={id ? "Edit Assemble Job" : "Add Assemble Job"}
            subtitle={
              id
                ? "Update an existing PC assembly job"
                : "Create a new PC assembly job request"
            }
          />
        </div>
      </PageHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Job Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Job Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="e.g. Custom Gaming PC Build"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  maxLength={255}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assemble_config">Assemble Config</Label>
                <ReactSelect
                  isLoading={configsLoading}
                  onInputChange={(val) => setConfigSearch(val)}
                  onMenuScrollToBottom={() => {
                    if (hasNextConfigsPage) fetchNextConfigsPage();
                  }}
                  filterOption={() => true}
                  value={(() => {
                    if (!formData.assemble_config_id) return null;
                    const c =
                      formData.assemble_config ||
                      configs.find((c) => c.id === formData.assemble_config_id);
                    const label = c ? c.name : "Select Config";
                    return { value: formData.assemble_config_id, label };
                  })()}
                  onChange={handleConfigChange}
                  options={configs.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  placeholder="Select Config to auto-fill..."
                  isClearable
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">Customer</Label>
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <ReactSelect
                      isLoading={customersLoading}
                      onInputChange={(val) => setCustomerSearch(val)}
                      onMenuScrollToBottom={() => {
                        if (hasNextCustomersPage) fetchNextCustomersPage();
                      }}
                      filterOption={() => true}
                      value={(() => {
                        if (!formData.customer_id) return null;
                        const c =
                          formData.customer ||
                          customers.find((c) => c.id === formData.customer_id);
                        const label = c
                          ? c.customer_type === "individual"
                            ? c.full_name
                            : c.company || c.name || c.full_name
                          : "Select Customer";
                        return { value: formData.customer_id, label };
                      })()}
                      onChange={(selectedOption) => {
                        const selectedCustomer = customers.find(
                          (c) => c.id === selectedOption?.value,
                        );
                        setFormData({
                          ...formData,
                          customer_id: selectedOption
                            ? selectedOption.value
                            : "",
                          customer: selectedCustomer || null,
                        });
                      }}
                      options={customers.map((c) => ({
                        value: c.id,
                        label:
                          c.customer_type === "individual"
                            ? c.full_name
                            : c.company || c.name || c.full_name,
                      }))}
                      placeholder="Select Customer (Optional)"
                    />
                  </div>
                  {hasPermission("customer create") && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setShowCustomerModal(true)}
                      title="Add New Customer"
                    >
                      <PlusCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assigned_employee">Assigned Employee</Label>
                <ReactSelect
                  isLoading={employeesLoading}
                  onInputChange={(val) => setEmployeeSearch(val)}
                  onMenuScrollToBottom={() => {
                    if (hasNextEmployeesPage) fetchNextEmployeesPage();
                  }}
                  filterOption={() => true}
                  value={(() => {
                    if (!formData.assigned_employee_id) return null;
                    const e =
                      formData.assigned_employee ||
                      employees.find(
                        (e) => e.id === formData.assigned_employee_id,
                      );
                    const label = e ? e.name || e.full_name : "Select Employee";
                    return { value: formData.assigned_employee_id, label };
                  })()}
                  onChange={(selectedOption) => {
                    const selectedEmployee = employees.find(
                      (e) => e.id === selectedOption?.value,
                    );
                    setFormData({
                      ...formData,
                      assigned_employee_id: selectedOption
                        ? selectedOption.value
                        : "",
                      assigned_employee: selectedEmployee || null,
                    });
                  }}
                  options={employees.map((e) => ({
                    value: e.id,
                    label: e.name || e.full_name,
                  }))}
                  placeholder="Select Employee (Optional)"
                  isClearable
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assembly_fee">Assembly Fee</Label>
                <Input
                  id="assembly_fee"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.assembly_fee}
                  onChange={(e) =>
                    setFormData({ ...formData, assembly_fee: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <DefaultEditor
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                containerProps={{ style: { minHeight: "250px" } }}
              />
            </div>

            <div className="flex items-center gap-6 pt-2">
              <div className="flex items-center space-x-2">
                <Switch
                  id="show_prices"
                  checked={formData.show_component_prices}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      show_component_prices: checked,
                    })
                  }
                />
                <Label htmlFor="show_prices" className="cursor-pointer">
                  Show Component Prices
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Components</CardTitle>
            <Button
              type="button"
              onClick={addItem}
              size="sm"
              variant="secondary"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Component
            </Button>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg bg-muted/20">
                No components added. Click "Add Component" to start building.
              </div>
            ) : (
              <div className="space-y-3">
                {/* Header row for md screens */}
                <div className="hidden md:flex items-center gap-4 px-2 pb-2 text-sm font-medium text-muted-foreground border-b mb-2">
                  <div className="w-2/5">Product</div>
                  <div className="w-16">Qty</div>
                  <div className="w-32">Unit Price</div>
                  <div className="flex-1">Config Details</div>
                  <div className="w-20 text-right">Actions</div>
                </div>

                <div>
                  {items.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex flex-col md:flex-row items-start md:items-center gap-4 p-4 md:p-2 bg-card border rounded-lg mb-3 shadow-sm md:shadow-none md:border-transparent md:bg-transparent"
                    >
                      {/* Product Select */}
                      <div className="w-full md:w-2/5">
                        <Label className="md:hidden mb-2 block">Product</Label>
                        <ReactSelect
                          isLoading={productsLoading}
                          onInputChange={(val) => setProductSearch(val)}
                          onMenuScrollToBottom={() => {
                            if (hasNextProductsPage) fetchNextProductsPage();
                          }}
                          filterOption={() => true}
                          value={(() => {
                            if (!item.product_id) return null;
                            const p =
                              item.product ||
                              products.find((p) => p.id === item.product_id);
                            const label = p
                              ? `${p.name}${p.sku ? ` (SKU: ${p.sku})` : ""}`
                              : "Select Product";
                            return { value: item.product_id, label };
                          })()}
                          onChange={(selectedOption) => {
                            const selectedProduct = products.find(
                              (p) => p.id === selectedOption?.value,
                            );
                            orderItemChange(
                              index,
                              "product_id",
                              selectedOption ? selectedOption.value : "",
                            );
                            orderItemChange(
                              index,
                              "product",
                              selectedProduct || null,
                            );
                            if (selectedProduct) {
                              openConfigModal(index, selectedProduct);
                            }
                          }}
                          options={products.map((product) => ({
                            value: product.id,
                            label: `${product.name}${product.sku ? ` (SKU: ${product.sku})` : ""}`,
                          }))}
                          placeholder="Select Product..."
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      </div>

                      {/* Quantity */}
                      <div className="w-full md:w-16">
                        <Label className="md:hidden mb-2 block">Qty</Label>
                        <span className="text-sm font-medium">
                          {item.quantity}
                        </span>
                      </div>

                      {/* Unit Price */}
                      <div className="w-full md:w-32">
                        <Label className="md:hidden mb-2 block">
                          Unit Price
                        </Label>
                        <span className="text-sm font-medium">
                          {currentStore?.default_currency?.symbol || "$"}
                          {Number(item.unit_price || 0).toFixed(2)}
                        </span>
                      </div>

                      {/* Details */}
                      <div className="w-full md:flex-1">
                        <Label className="md:hidden mb-2 block">Details</Label>
                        <div className="flex flex-col gap-1 text-xs">
                          {item.inventory_batch_id && (
                            <span className="text-muted-foreground">
                              <span className="font-semibold">Batch:</span>{" "}
                              {item.product?.batches?.find(
                                (b) => b.id === item.inventory_batch_id,
                              )?.batch_number || item.inventory_batch_id}
                            </span>
                          )}
                          {item.inventory_serial_id && (
                            <span className="text-muted-foreground">
                              <span className="font-semibold">Serial:</span>{" "}
                              {(
                                item.product?.serials ||
                                item.product?.serial_numbers
                              )?.find((s) => s.id === item.inventory_serial_id)
                                ?.serial_number || item.inventory_serial_id}
                            </span>
                          )}
                          {!item.inventory_batch_id &&
                            !item.inventory_serial_id && (
                              <span className="text-muted-foreground italic">
                                No batch or serial selected
                              </span>
                            )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="w-full md:w-20 flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="hover:bg-primary/10"
                          onClick={() => openConfigModal(index)}
                          disabled={!item.product_id}
                        >
                          <Settings className="h-4 w-4 text-primary" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end gap-3 pt-6 border-t mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createJobMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {createJobMutation.isPending ? "Saving..." : "Save Job"}
            </Button>
          </CardFooter>
        </Card>
      </form>

      {showConfigModal && currentItemConfig && (
        <Modal
          open={showConfigModal}
          onOpenChange={(open) => !open && setShowConfigModal(false)}
          title={editingIndex !== null ? "Edit Item" : "Add Item"}
          maxWidth="max-w-2xl"
          footer={
            <div className="flex w-full justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConfigModal(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveItemConfig}>
                {editingIndex !== null ? "Update Item" : "Add to Job"}
              </Button>
            </div>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="col-span-full space-y-2">
              <Label>
                Product <span className="text-destructive">*</span>
              </Label>
              <Input
                value={currentItemConfig.product?.name || ""}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label>
                Quantity <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                placeholder="Enter quantity"
                value={currentItemConfig.quantity}
                onChange={(e) =>
                  setCurrentItemConfig({
                    ...currentItemConfig,
                    quantity: parseInt(e.target.value) || 1,
                  })
                }
                min="1"
                disabled={currentItemConfig.product?.is_serialized}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Unit Price <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                placeholder="Enter unit price"
                value={currentItemConfig.unit_price}
                onChange={(e) =>
                  setCurrentItemConfig({
                    ...currentItemConfig,
                    unit_price: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
              />
            </div>

            {currentItemConfig.product &&
              (currentItemConfig.product.tracking_type === "standard" ||
                currentItemConfig.product.is_serialized) && (
                <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 mt-2">
                  {currentItemConfig.product.tracking_type === "standard" && (
                    <div className="space-y-2 col-span-full">
                      <Label>
                        Select Batch <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        options={(currentItemConfig.product.batches || []).map(
                          (b) => ({
                            value: b.id,
                            label: `${b.batch_number} - Exp: ${b.expiry_date || "N/A"
                              } (Stock: ${b.quantity})`,
                          }),
                        )}
                        value={(currentItemConfig.product.batches || [])
                          .map((b) => ({
                            value: b.id,
                            label: `${b.batch_number} - Exp: ${b.expiry_date || "N/A"
                              } (Stock: ${b.quantity})`,
                          }))
                          .find(
                            (opt) =>
                              opt.value ===
                              currentItemConfig.inventory_batch_id,
                          )}
                        onChange={(opt) =>
                          setCurrentItemConfig({
                            ...currentItemConfig,
                            inventory_batch_id: opt?.value,
                          })
                        }
                        placeholder="Select a batch"
                        maxMenuHeight={200}
                      />
                    </div>
                  )}

                  {currentItemConfig.product.is_serialized && (
                    <div className="space-y-2 col-span-full">
                      <Label>
                        Select Serial Numbers (Selected:{" "}
                        {currentItemConfig.serial_numbers?.length || 0}){" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        isMulti
                        options={(
                          currentItemConfig.product.serials ||
                          currentItemConfig.product.serial_numbers ||
                          []
                        ).map((s) => ({
                          value: s.id,
                          label: s.serial_number,
                          serialNumber: s.serial_number,
                        }))}
                        value={(currentItemConfig.serial_numbers || []).map(
                          (sn) => {
                            const serialObj = (
                              currentItemConfig.product.serials ||
                              currentItemConfig.product.serial_numbers
                            )?.find(
                              (s) => s.id === sn || s.serial_number === sn,
                            );
                            return {
                              value: serialObj?.id || sn,
                              label: serialObj?.serial_number || sn,
                              serialNumber: serialObj?.serial_number || sn,
                            };
                          },
                        )}
                        onChange={(opts) => {
                          const selectedSerials = opts
                            ? opts.map((o) => o.value)
                            : [];
                          setCurrentItemConfig({
                            ...currentItemConfig,
                            serial_numbers: selectedSerials,
                            quantity:
                              selectedSerials.length > 0
                                ? selectedSerials.length
                                : 1,
                          });
                        }}
                        placeholder="Select serial numbers"
                        maxMenuHeight={250}
                      />
                      <p className="text-xs text-muted-foreground">
                        For serialized products, quantity is determined by the
                        number of serials selected.
                      </p>
                    </div>
                  )}
                </div>
              )}

            <div className="col-span-full mt-2">
              <Card className="bg-muted/50">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Base Price:</span>
                    <span>
                      {currentStore?.default_currency?.symbol || "$"}
                      {(
                        (parseFloat(currentItemConfig.unit_price) || 0) *
                        (parseInt(currentItemConfig.quantity) || 1)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="pt-2 border-t flex justify-between items-center">
                    <span className="font-bold">Line Total:</span>
                    <span className="font-bold text-lg">
                      {currentStore?.default_currency?.symbol || "$"}
                      {(
                        (parseFloat(currentItemConfig.unit_price) || 0) *
                        (parseInt(currentItemConfig.quantity) || 1)
                      ).toFixed(2)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </Modal>
      )}
      {/* Customer Modal */}
      <CustomerModal
        open={showCustomerModal}
        onOpenChange={setShowCustomerModal}
      />
    </PageContainer>
  );
};

export default AddAssembleJob;
